// src/services/azureProjectsService.js
// Azure Table Storage Service für Projektdaten
import * as RelationsService from "./azureRelationsService";
import * as TagsService from "./azureTagsService";

const AZURE_CONFIG = {
  storageAccount: "mfprojects",
  tableName: "projects",
  sasToken:
    "sv=2024-11-04&ss=t&srt=sco&sp=rwdlacu&se=2035-07-04T17:08:25Z&st=2025-07-04T09:08:25Z&spr=https&sig=X7s%2BFIyYcKvRzSxRNAp5BUJEe2YUcg8nojkn9fIfcTY%3D",
};

// Hilfsfunktion für eindeutige IDs
const generateProjectId = () => {
  return Date.now().toString();
};

// Alle Projekte laden
export const getAllProjects = async () => {
  try {
    let allProjects = [];
    let nextPartitionKey = null;
    let nextRowKey = null;

    do {
      let url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}?${AZURE_CONFIG.sasToken}`;

      if (nextPartitionKey && nextRowKey) {
        url += `&NextPartitionKey=${encodeURIComponent(nextPartitionKey)}`;
        url += `&NextRowKey=${encodeURIComponent(nextRowKey)}`;
      }

      const response = await fetch(url, {
        headers: { Accept: "application/json;odata=nometadata" },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Azure Error:", errorText);
        throw new Error(`Azure Table Error: ${response.status}`);
      }

      const data = await response.json();
      allProjects = allProjects.concat(data.value || []);

      nextPartitionKey = response.headers.get(
        "x-ms-continuation-NextPartitionKey"
      );
      nextRowKey = response.headers.get("x-ms-continuation-NextRowKey");
    } while (nextPartitionKey && nextRowKey);

    // Konvertiere Azure Table Format zu Frontend Format
    const projects = allProjects.map((item) => ({
      id: item.RowKey,
      code: item.Code,
      public: item.Public,
      comingSoon: item.ComingSoon || false,
      // GL-Schalter "externe Projektwebsite" (US-WEBP-001, ADR 0045)
      projektwebsite: item.Projektwebsite === true || item.Projektwebsite === "true",
      title: item.Title,
      type: item.Type,
      longtitle: item.Longtitle,
      projektstart: item.Projektstart,
      projektende: item.Projektende,
      baukosten: item.Baukosten || 0,
      honorar: item.Honorar || 0,
      scale: item.Scale || "",
      websiteUrl: item.WebsiteUrl || "",
      importance: item.Importance || 0,
      beschreibung: item.Beschreibung || "",
      adresse: item.Adresse || "",
      color: item.Color || "#059669",
      location: {
        lat: item.Latitude || null,
        lng: item.Longitude || null,
      },
      auftraggeberin: item.auftraggeberin || "",
      localOnly: item.LocalOnly === true || item.LocalOnly === "true",
      tags: [],
      createdAt: item.CreatedAt,
      updatedAt: item.UpdatedAt,
    }));

    return projects;
  } catch (error) {
    console.error("Load Projects Error:", error);
    return [];
  }
};

export const getAllProjectsAndOfferten = async () => {
  try {
    const projects = await getAllProjects(); // Lädt ALLE ohne Filter

    // Für jedes Projekt Tags laden
    const projectsWithTags = await Promise.all(
      projects.map(async (project) => {
        try {
          return await getProjectWithTags(project.id);
        } catch (error) {
          console.warn(
            `Fehler beim Laden der Tags für Projekt ${project.id}:`,
            error
          );
          return { ...project, tags: [] };
        }
      })
    );

    return projectsWithTags;
  } catch (error) {
    console.error("Fehler beim Laden aller Projekte und Offerten:", error);
    return [];
  }
};

// Einzelnes Projekt laden
export const getProject = async (projectId) => {
  try {
    const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}(PartitionKey='project',RowKey='${projectId}')?${AZURE_CONFIG.sasToken}`;

    const response = await fetch(url, {
      headers: { Accept: "application/json;odata=nometadata" },
    });

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Azure Table Error: ${response.status}`);
    }

    const item = await response.json();

    return {
      id: item.RowKey,
      code: item.Code,
      public: item.Public,
      comingSoon: item.ComingSoon || false,
      // GL-Schalter "externe Projektwebsite" (US-WEBP-001, ADR 0045)
      projektwebsite: item.Projektwebsite === true || item.Projektwebsite === "true",
      title: item.Title,
      type: item.Type,
      longtitle: item.Longtitle,
      projektstart: item.Projektstart,
      projektende: item.Projektende,
      baukosten: item.Baukosten || 0,
      honorar: item.Honorar || 0,
      scale: item.Scale || "",
      websiteUrl: item.WebsiteUrl || "",
      importance: item.Importance || 0,
      beschreibung: item.Beschreibung || "",
      adresse: item.Adresse || "",
      color: item.Color || "#059669",
      location: {
        lat: item.Latitude || null,
        lng: item.Longitude || null,
      },
      auftraggeberin: item.auftraggeberin || "",
      localOnly: item.LocalOnly === true || item.LocalOnly === "true",
      tags: [],
      createdAt: item.CreatedAt,
      updatedAt: item.UpdatedAt,
    };
  } catch (error) {
    console.error("Load Project Error:", error);
    return null;
  }
};

// Projekt erstellen
export const createProject = async (projectData) => {
  try {
    const projectId = generateProjectId();
    const now = new Date().toISOString();

    // Validate required fields
    if (
      (projectData.type === "project" || projectData.type === "offerte") &&
      !projectData.code
    ) {
      throw new Error("code ist für Projekte und Offerten erforderlich");
    }
    if (!projectData.title) {
      throw new Error("Titel ist erforderlich");
    }

    // Check if code already exists
    if (projectData.type === "project" || projectData.type === "offerte") {
      const existingProjects = await getAllProjects();
      const numberExists = existingProjects.some(
        (p) => p.code === projectData.code
      );
      if (numberExists) {
        throw new Error(`code ${projectData.code} ist bereits vergeben`);
      }
    }

    // Helper function to clean values for Azure Table Storage
    const cleanValue = (value) => {
      if (value === null || value === undefined || value === "") {
        return "";
      }
      return String(value);
    };

    const cleanNumber = (value) => {
      if (
        value === null ||
        value === undefined ||
        value === "" ||
        isNaN(value)
      ) {
        return 0;
      }
      return Number(value);
    };

    const entity = {
      PartitionKey: "project",
      RowKey: projectId,
      Code: cleanValue(projectData.code),
      Public: projectData.public,
      ComingSoon: projectData.comingSoon || false,
      Projektwebsite: projectData.projektwebsite === true,
      Title: cleanValue(projectData.title),
      Type: cleanValue(projectData.type),
      Longtitle: cleanValue(projectData.longtitle),
      Projektstart: cleanValue(projectData.projektstart),
      Projektende: cleanValue(projectData.projektende),
      Baukosten: cleanNumber(projectData.baukosten),
      Honorar: cleanNumber(projectData.honorar),
      Scale: cleanValue(projectData.scale),
      WebsiteUrl: cleanValue(projectData.websiteUrl),
      Importance: cleanNumber(projectData.importance),
      Beschreibung: cleanValue(projectData.beschreibung),
      Adresse: cleanValue(projectData.adresse),
      Color: projectData.color || "#059669",
      Latitude: projectData.location?.lat
        ? Number(projectData.location.lat)
        : null,
      Longitude: projectData.location?.lng
        ? Number(projectData.location.lng)
        : null,
      auftraggeberin: cleanValue(projectData.auftraggeberin),
      LocalOnly: projectData.localOnly || false,
      CreatedAt: now,
      UpdatedAt: now,
    };

    // Remove null values (Azure Table Storage doesn't like null)
    Object.keys(entity).forEach((key) => {
      if (entity[key] === null) {
        delete entity[key];
      }
    });

    const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}(PartitionKey='project',RowKey='${projectId}')?${AZURE_CONFIG.sasToken}`;

    const response = await fetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
      },
      body: JSON.stringify(entity),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Azure Error Details:", errorText);
      throw new Error(`Azure Save Error: ${response.status} - ${errorText}`);
    }

    return {
      id: projectId,
      ...projectData,
      type: projectData.type || "project",
      createdAt: now,
      updatedAt: now,
    };
  } catch (error) {
    console.error("Create Project Error:", error);
    throw error;
  }
};

// Projekt aktualisieren
export const updateProject = async (projectId, projectData) => {
  try {
    const now = new Date().toISOString();

    // Validate required fields
    if (
      (projectData.type === "project" || projectData.type === "offerte") &&
      !projectData.code
    ) {
      throw new Error("code ist für Projekte und Offerten erforderlich");
    }
    if (!projectData.title) {
      throw new Error("Titel ist erforderlich");
    }

    // Helper function to clean values for Azure Table Storage
    const cleanValue = (value) => {
      if (value === null || value === undefined || value === "") {
        return "";
      }
      return String(value);
    };

    const cleanNumber = (value) => {
      if (
        value === null ||
        value === undefined ||
        value === "" ||
        isNaN(value)
      ) {
        return 0;
      }
      return Number(value);
    };

    const entity = {
      PartitionKey: "project",
      RowKey: projectId,
      Code: cleanValue(projectData.code),
      Public: projectData.public,
      ComingSoon: projectData.comingSoon,
      Projektwebsite: projectData.projektwebsite === true,
      Title: cleanValue(projectData.title),
      Longtitle: cleanValue(projectData.longtitle),
      Projektstart: cleanValue(projectData.projektstart),
      Projektende: cleanValue(projectData.projektende),
      Baukosten: cleanNumber(projectData.baukosten),
      Honorar: cleanNumber(projectData.honorar),
      Scale: cleanValue(projectData.scale),
      WebsiteUrl: cleanValue(projectData.websiteUrl),
      Importance: cleanNumber(projectData.importance),
      Beschreibung: cleanValue(projectData.beschreibung),
      Adresse: cleanValue(projectData.adresse),
      Type: cleanValue(projectData.type || ""),
      Color: projectData.color || "#059669",
      Latitude: projectData.location?.lat
        ? Number(projectData.location.lat)
        : null,
      Longitude: projectData.location?.lng
        ? Number(projectData.location.lng)
        : null,
      auftraggeberin: cleanValue(projectData.auftraggeberin),
      LocalOnly: projectData.localOnly || false,
      UpdatedAt: now,
    };

    // Remove null values (Azure Table Storage doesn't like null)
    Object.keys(entity).forEach((key) => {
      if (entity[key] === null) {
        delete entity[key];
      }
    });

    const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}(PartitionKey='project',RowKey='${projectId}')?${AZURE_CONFIG.sasToken}`;

    const response = await fetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
      },
      body: JSON.stringify(entity),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Azure Error Details:", errorText);
      throw new Error(`Azure Update Error: ${response.status} - ${errorText}`);
    }

    return {
      id: projectId,
      ...projectData,
      type: projectData.type || "",
      updatedAt: now,
    };
  } catch (error) {
    console.error("Update Project Error:", error);
    throw error;
  }
};

// Projekt löschen
export const deleteProject = async (projectId) => {
  try {
    const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}(PartitionKey='project',RowKey='${projectId}')?${AZURE_CONFIG.sasToken}`;

    const response = await fetch(url, {
      method: "DELETE",
      headers: {
        "If-Match": "*",
      },
    });

    if (!response.ok && response.status !== 404) {
      const errorText = await response.text();
      throw new Error(`Azure Delete Error: ${response.status} - ${errorText}`);
    }

    return true;
  } catch (error) {
    console.error("Delete Project Error:", error);
    throw error;
  }
};

// Projekte nach PIC laden
export const getProjectsByPIC = async (employeeUuid) => {
  try {
    const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}?${AZURE_CONFIG.sasToken}&$filter=PIC eq '${employeeUuid}'`;

    const response = await fetch(url, {
      headers: { Accept: "application/json;odata=nometadata" },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Azure Table Error: ${response.status}`);
    }

    const data = await response.json();

    const projects = data.value.map((item) => ({
      id: item.RowKey,
      Code: item.code,
      public: item.Public,
      comingSoon: item.ComingSoon,
      projektwebsite: item.Projektwebsite === true || item.Projektwebsite === "true",
      title: item.Title,
      type: item.Type,
      longtitle: item.Longtitle,
      projektstart: item.Projektstart,
      projektende: item.Projektende,
      baukosten: item.Baukosten || 0,
      honorar: item.Honorar || 0,
      scale: item.Scale || "",
      websiteUrl: item.WebsiteUrl || "",
      importance: item.Importance || 0,
      beschreibung: item.Beschreibung || "",
      adresse: item.Adresse || "",
      color: item.Color || "#059669",
      location: {
        lat: item.Latitude || null,
        lng: item.Longitude || null,
      },
      auftraggeberin: item.auftraggeberin || "",
      localOnly: item.LocalOnly === true || item.LocalOnly === "true",
      tags: [],
      createdAt: item.CreatedAt,
      updatedAt: item.UpdatedAt,
    }));

    return projects;
  } catch (error) {
    console.error("Load Projects by PIC Error:", error);
    return [];
  }
};

// Projekt nach Nummer suchen
export const getProjectByNumber = async (code) => {
  try {
    const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}?${AZURE_CONFIG.sasToken}&$filter=Code eq '${code}'`;

    const response = await fetch(url, {
      headers: { Accept: "application/json;odata=nometadata" },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Azure Table Error: ${response.status}`);
    }

    const data = await response.json();

    if (data.value.length === 0) {
      return null;
    }

    const item = data.value[0];
    return {
      id: item.RowKey,
      Code: item.code,
      public: item.Public,
      comingSoon: item.ComingSoon,
      projektwebsite: item.Projektwebsite === true || item.Projektwebsite === "true",
      title: item.Title,
      type: item.Type,
      longtitle: item.Longtitle,
      projektstart: item.Projektstart,
      projektende: item.Projektende,
      baukosten: item.Baukosten || 0,
      honorar: item.Honorar || 0,
      scale: item.Scale || "",
      websiteUrl: item.WebsiteUrl || "",
      importance: item.Importance || 0,
      beschreibung: item.Beschreibung || "",
      adresse: item.Adresse || "",
      color: item.Color || "#059669",
      location: {
        lat: item.Latitude || null,
        lng: item.Longitude || null,
      },
      auftraggeberin: item.auftraggeberin || "",
      localOnly: item.LocalOnly === true || item.LocalOnly === "true",
      tags: [],
      createdAt: item.CreatedAt,
      updatedAt: item.UpdatedAt,
    };
  } catch (error) {
    console.error("Get Project by Number Error:", error);
    return null;
  }
};

// Zahlungsplan-Einstellungen laden (aus ZahlungsplanSettings JSON-Feld)
export const getZahlungsplanSettings = async (projectId) => {
  try {
    const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}(PartitionKey='project',RowKey='${projectId}')?${AZURE_CONFIG.sasToken}&$select=ZahlungsplanSettings`;

    const response = await fetch(url, {
      headers: { Accept: "application/json;odata=nometadata" },
    });

    if (!response.ok) return null;

    const item = await response.json();
    if (!item.ZahlungsplanSettings) return null;

    try {
      return JSON.parse(item.ZahlungsplanSettings);
    } catch {
      return null;
    }
  } catch (error) {
    console.error("Load ZahlungsplanSettings Error:", error);
    return null;
  }
};

// Zahlungsplan-Einstellungen speichern (MERGE – überschreibt nur dieses Feld)
export const saveZahlungsplanSettings = async (projectId, settings) => {
  try {
    const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}(PartitionKey='project',RowKey='${projectId}')?${AZURE_CONFIG.sasToken}`;

    const entity = {
      PartitionKey: "project",
      RowKey: projectId,
      ZahlungsplanSettings: JSON.stringify(settings),
    };

    const response = await fetch(url, {
      method: "MERGE",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
        "If-Match": "*",
      },
      body: JSON.stringify(entity),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Azure MERGE Error:", errorText);
      throw new Error(`Azure MERGE Error: ${response.status}`);
    }

    return true;
  } catch (error) {
    console.error("Save ZahlungsplanSettings Error:", error);
    throw error;
  }
};

// Konfiguration aktualisieren (für andere SAS Token etc.)
export const updateConfig = (newConfig) => {
  Object.assign(AZURE_CONFIG, newConfig);
};

export const getProjectWithTags = async (projectId) => {
  try {
    // 1. Projekt laden
    const project = await getProject(projectId);
    if (!project) return null;

    // 2. Tag-Relations laden
    const relations = await RelationsService.getProjectRelations(projectId);
    const tagRelations = relations.filter((rel) => rel.targetType === "tag");

    // 3. Tag-Details laden
    const tags = [];
    for (const relation of tagRelations) {
      try {
        const tag = await TagsService.getTag(relation.targetId);
        if (tag) {
          tags.push(tag);
        }
      } catch (error) {
        console.warn(`Tag ${relation.targetId} nicht gefunden:`, error);
      }
    }

    // 4. Projekt mit Tags zurückgeben
    return {
      ...project,
      tags: tags,
    };
  } catch (error) {
    console.error("Fehler beim Laden des Projekts mit Tags:", error);
    return project; // Fallback ohne Tags
  }
};

// Alle Projekte mit Tags laden
export const getAllProjectsWithTags = async (typeFilter = "project") => {
  try {
    const projects = await getAllProjects();

    let filteredProjects;

    if (typeFilter === "all") {
      // Alle Projekte ohne Filter
      filteredProjects = projects;
    } else if (typeFilter === "other") {
      // Alles ausser project und offerte
      filteredProjects = projects.filter(
        (project) =>
          project.type &&
          project.type !== "project" &&
          project.type !== "offerte"
      );
    } else {
      // Spezifischer Type-Filter
      filteredProjects = projects.filter(
        (project) =>
          project.type === typeFilter ||
          (!project.type && typeFilter === "project")
      );
    }

    // Tags für gefilterte Projekte laden
    const projectsWithTags = await Promise.all(
      filteredProjects.map(async (project) => {
        try {
          return await getProjectWithTags(project.id);
        } catch (error) {
          console.warn(
            `Fehler beim Laden der Tags für Projekt ${project.id}:`,
            error
          );
          return { ...project, tags: [] };
        }
      })
    );

    return projectsWithTags;
  } catch (error) {
    console.error("Fehler beim Laden aller Projekte:", error);
    return [];
  }
};

// Projekt mit Tags speichern
export const saveProjectWithTags = async (projectData) => {
  try {
    let savedProject;

    if (projectData.id) {
      savedProject = await updateProject(projectData.id, projectData);
    } else {
      savedProject = await createProject(projectData);
    }

    // HIER: savedProject.id muss existieren!
    console.log("Saved Project:", savedProject);
    console.log("Tags to save:", projectData.tags);

    if (projectData.tags && projectData.tags.length > 0) {
      await saveProjectTagRelations(savedProject.id, projectData.tags);
    } else {
      await saveProjectTagRelations(savedProject.id, []);
    }

    return savedProject;
  } catch (error) {
    console.error("saveProjectWithTags Error:", error);
    throw error;
  }
};

// Tag-Relations für Projekt speichern
const saveProjectTagRelations = async (projectId, tags) => {
  try {
    // Validierung
    if (!projectId) {
      console.error("saveProjectTagRelations: Keine projectId");
      return;
    }

    console.log("saveProjectTagRelations:", projectId, tags);

    // Bestehende Tag-Relations löschen
    const existingRelations = await RelationsService.getProjectRelations(
      projectId
    );
    const tagRelations = existingRelations.filter(
      (rel) => rel.targetType === "tag"
    );

    for (const relation of tagRelations) {
      await RelationsService.deleteRelation(relation.id);
      await TagsService.decrementTagUsage(relation.targetId);
    }

    // Tags validieren
    if (!tags || tags.length === 0) {
      console.log("Keine Tags zu speichern");
      return;
    }

    const validTags = tags.filter((tag) => tag && tag.id);

    if (validTags.length !== tags.length) {
      console.warn(
        `${tags.length - validTags.length} ungültige Tags ignoriert`
      );
    }

    // Neue Tag-Relations erstellen
    for (const tag of validTags) {
      const relationData = {
        sourceType: "project",
        sourceId: projectId,
        targetType: "tag",
        targetId: tag.id,
        relationType: RelationsService.RELATION_TYPES.PROJECT_TAG,
        function: "",
        description: `Tag: ${tag.name}`,
      };

      console.log("Creating relation:", relationData);
      await RelationsService.createRelation(relationData);
      await TagsService.incrementTagUsage(tag.id);
    }

    console.log(`${validTags.length} Tag-Relations erstellt`);
  } catch (error) {
    console.error("Fehler saveProjectTagRelations:", error);
    throw error;
  }
};
